import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield } from "lucide-react";

const ROLES = [
  { v: "user", label: "Operatör" },
  { v: "admin", label: "Yönetici" },
  { v: "supervisor", label: "Supervisor" },
];

const inputCls =
  "w-full h-10 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none";

const EMPTY = { email: "", password: "", name: "", role: "user" };

function UserRow({ u, me, onRole, onRemove }) {
  const self = u.id === me.id;
  return (
    <tr data-testid="user-row">
      <td className="font-mono">{u.email}</td>
      <td>{u.name}</td>
      <td>
        {self ? (
          <span className="inline-block px-2 py-0.5 hard text-[10px] font-bold uppercase bg-[color:var(--primary)] text-white">
            {u.role}
          </span>
        ) : (
          <select
            data-testid="user-role-select"
            value={u.role}
            onChange={(e) => onRole(u, e.target.value)}
            className="h-8 px-2 hard border border-[color:var(--border)] bg-white text-xs font-semibold"
          >
            {ROLES.map((r) => (
              <option key={r.v} value={r.v}>{r.label}</option>
            ))}
          </select>
        )}
      </td>
      <td className="text-[color:var(--text-2)]">
        {u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : ""}
      </td>
      <td>
        {!self && (
          <button
            onClick={() => onRemove(u.id)}
            data-testid="user-delete"
            className="p-1.5 hard hover:bg-[color:var(--error-bg)] hover:text-[color:var(--error)]"
          >
            <Trash2 size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const allowed = me?.role === "supervisor";

  const load = () => {
    setLoading(true);
    api
      .get("/users")
      .then(({ data }) => setUsers(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (allowed) load();
  }, [allowed]);

  if (!allowed) {
    return (
      <div data-testid="users-forbidden" className="hard border border-[color:var(--warning)] bg-[color:var(--warning-bg)] text-[color:var(--warning)] p-4">
        Bu sayfa yalnızca Supervisor içindir.
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", form);
      toast.success("Kullanıcı oluşturuldu");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeRole = async (u, role) => {
    if (!window.confirm(`${u.email} kullanıcısının rolü "${role}" olarak değiştirilsin mi?`)) return;
    try {
      await api.put(`/users/${u.id}/role`, { role });
      toast.success(`${u.email} rolü güncellendi`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kullanıcıyı silmek istiyor musunuz?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4">
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] overflow-x-auto">
        <div className="p-4 border-b border-[color:var(--border)] flex items-center gap-2">
          <Shield size={18} />
          <h3 className="font-bold">Kullanıcılar</h3>
          <span className="ticker text-[color:var(--text-3)] ml-auto">{users.length} kayıt</span>
        </div>
        <table className="dense-table w-full">
          <thead>
            <tr>
              <th>E-Posta</th>
              <th>İsim</th>
              <th>Rol</th>
              <th>Oluşturuldu</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="text-center py-8 text-[color:var(--text-3)]">Yükleniyor…</td>
              </tr>
            )}
            {users.map((u) => (
              <UserRow key={u.id} u={u} me={me} onRole={changeRole} onRemove={remove} />
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={submit} className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 space-y-4 h-fit">
        <div className="flex items-center gap-2">
          <UserPlus size={18} className="text-[color:var(--accent)]" />
          <h3 className="font-bold">Yeni Kullanıcı</h3>
        </div>
        <div>
          <label className="ticker block mb-1 text-[color:var(--text-2)]">E-Posta</label>
          <input data-testid="new-user-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
        </div>
        <div>
          <label className="ticker block mb-1 text-[color:var(--text-2)]">İsim</label>
          <input data-testid="new-user-name" value={form.name} onChange={set("name")} className={inputCls} />
        </div>
        <div>
          <label className="ticker block mb-1 text-[color:var(--text-2)]">Şifre</label>
          <input data-testid="new-user-password" type="password" required minLength={4} value={form.password} onChange={set("password")} className={inputCls} />
        </div>
        <div>
          <label className="ticker block mb-1 text-[color:var(--text-2)]">Rol</label>
          <select data-testid="new-user-role" value={form.role} onChange={set("role")} className={inputCls}>
            {ROLES.map((r) => (
              <option key={r.v} value={r.v}>{r.label}</option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          data-testid="new-user-submit"
          className="w-full h-11 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider"
        >
          Oluştur
        </button>
      </form>
    </div>
  );
}
